import useSWRMutation from 'swr/mutation';
import type { SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';

type ConsumePasswordResetRequest = {
  token: string;
  newPassword: string;
};

type ConsumePasswordResetResponse =
  | { success: true }
  | {
      success: false;
      message: string;
      code: 'INVALID_OR_EXPIRED_TOKEN' | 'RESET_FAILED';
    };

const fetcher = async (
  _key: string,
  { arg }: { arg: ConsumePasswordResetRequest }
): Promise<ConsumePasswordResetResponse> =>
  await denApi.post<ConsumePasswordResetResponse, { newPassword: string }>(
    `password-reset/consume/${encodeURIComponent(arg.token)}`,
    { newPassword: arg.newPassword }
  );

/**
 * Hook to consume a password reset token and set a new password.
 * Returns success/failure with appropriate error messages.
 */
export function useConsumePasswordReset(
  swrOptions?: SWRMutationConfiguration<
    ConsumePasswordResetResponse,
    Error,
    string,
    ConsumePasswordResetRequest
  >
) {
  const { trigger, isMutating, error, reset } = useSWRMutation<
    ConsumePasswordResetResponse,
    Error,
    string,
    ConsumePasswordResetRequest
  >('password-reset/consume', fetcher, swrOptions);

  return {
    consumeReset: trigger,
    isLoading: isMutating,
    error,
    clearError: reset,
  };
}
